/**
 * QA API Client
 * 
 * Provides functions for QA checklist management, formatting verification,
 * grammar checking, and pre-submission validation.
 */

import { getAPIClient } from './client';

export interface QAChecklistItem {
  id: string;
  text: string;
  mandatory: boolean;
  description?: string;
  rubric_score?: boolean; // If true, requires 1-5 score from reviewer
}

export interface QAChecklistCategory {
  id: string;
  name: string;
  items: QAChecklistItem[];
}

export interface QAChecklistConfig {
  categories: QAChecklistCategory[];
}

export interface QAChecklistItemStatus {
  completed: boolean;
  completed_at?: string;
  notes?: string;
  reviewer_notes?: string;
  approval_score?: number; // 1-5 for reviewer items
}

export interface QAChecklistStatus {
  total_items: number;
  completed_items: number;
  completion_percentage: number;
  all_completed: boolean;
  checklist_data: {
    items?: {
      [categoryId: string]: {
        [itemId: string]: QAChecklistItemStatus;
      };
    };
    completion_percentage?: number;
    total_items?: number;
    completed_items?: number;
    completed_at?: string;
    completed_by?: string;
    overall_score?: number; // For reviewer checklist
  };
}

export interface QAChecklistResponse {
  config: QAChecklistConfig;
  status: QAChecklistStatus;
}

export interface ChecklistItemUpdate {
  category_id: string;
  item_id: string;
  completed: boolean;
  notes?: string;
  rubric_score?: number; // 1-5 for reviewer items
}

export interface ChecklistBatchUpdate {
  completed: boolean;
  default_rubric_score?: number; // Default rubric score (1-5) for reviewer items requiring scores
}

export interface ChecklistBatchUpdateResponse {
  updated_count: number;
  status: QAChecklistStatus;
}

export interface GrammarError {
  type: 'grammar' | 'spelling' | 'tone' | 'style';
  severity: 'error' | 'warning' | 'suggestion';
  line_number?: number;
  position?: { start: number; end: number };
  original_text: string;
  suggested_text: string;
  explanation: string;
}

export interface ToneSuggestion {
  section: string;
  current_tone: string;
  suggested_tone: string;
  reason: string;
}

export interface StyleInconsistency {
  issue: string;
  occurrences: number;
  recommendation: string;
}

export interface GrammarCheckResult {
  checked_at: string;
  checked_by?: string;
  errors: GrammarError[];
  tone_suggestions: ToneSuggestion[];
  style_inconsistencies: StyleInconsistency[];
  readability_score?: number;
  overall_passed: boolean;
  error?: string;
}

export interface FormattingRequirementResult {
  passed: boolean;
  issues?: string[];
  current?: number;
  limit?: number;
  exceeded_by?: number;
}

export interface FormattingValidationResult {
  validated_at: string;
  validated_by?: string;
  requirements: {
    page_layout?: FormattingRequirementResult;
    fonts?: FormattingRequirementResult;
    headings?: FormattingRequirementResult;
    paragraphs?: FormattingRequirementResult;
    lists?: FormattingRequirementResult;
    tables_figures?: FormattingRequirementResult;
    word_count?: FormattingRequirementResult;
  };
  overall_passed: boolean;
  fix_recommendations: string[];
  error?: string;
}

export interface PreSubmissionReport {
  generated_at: string;
  generated_by: string;
  checklist_completion: {
    writer_checklist: {
      completed: boolean;
      percentage: number;
      total_items: number;
      completed_items: number;
    };
    reviewer_checklist: {
      completed: boolean;
      percentage: number;
      total_items: number;
      completed_items: number;
    };
  };
  formatting: FormattingValidationResult;
  grammar: GrammarCheckResult;
  overall_ready: boolean;
  blocking_issues: string[];
  recommendations: string[];
}

export interface TransitionValidation {
  allowed: boolean;
  blocking_issues: string[];
}

/**
 * Get QA checklist configuration and status
 */
export async function getQAChecklist(
  reportId: string,
  checklistType: 'writer' | 'reviewer'
): Promise<QAChecklistResponse> {
  const client = getAPIClient();
  return client.get<QAChecklistResponse>(
    `/reports/${reportId}/qa/checklist/${checklistType}`
  );
}

/**
 * Update checklist item completion
 */
export async function updateChecklistItem(
  reportId: string,
  checklistType: 'writer' | 'reviewer',
  update: ChecklistItemUpdate
): Promise<QAChecklistStatus> {
  const client = getAPIClient();
  return client.patch<QAChecklistStatus>(
    `/reports/${reportId}/qa/checklist/${checklistType}/items`,
    update
  );
}

/**
 * Batch update all checklist items at once
 * 
 * This is much more efficient than calling updateChecklistItem() for each item,
 * as it avoids race conditions and reduces network overhead.
 */
export async function batchUpdateChecklist(
  reportId: string,
  checklistType: 'writer' | 'reviewer',
  update: ChecklistBatchUpdate
): Promise<ChecklistBatchUpdateResponse> {
  const client = getAPIClient();
  return client.post<ChecklistBatchUpdateResponse>(
    `/reports/${reportId}/qa/checklist/${checklistType}/batch`,
    update
  );
}

/**
 * Check grammar, spelling, tone, and style
 */
export async function checkGrammar(reportId: string): Promise<GrammarCheckResult> {
  const client = getAPIClient();
  return client.post<GrammarCheckResult>(
    `/reports/${reportId}/qa/grammar/check`
  );
}

/**
 * Verify formatting against requirements
 */
export async function verifyFormatting(
  reportId: string
): Promise<FormattingValidationResult> {
  const client = getAPIClient();
  return client.post<FormattingValidationResult>(
    `/reports/${reportId}/qa/formatting/verify`
  );
}

/**
 * Run comprehensive pre-submission validation
 */
export async function validatePreSubmission(
  reportId: string
): Promise<PreSubmissionReport> {
  const client = getAPIClient();
  return client.post<PreSubmissionReport>(
    `/reports/${reportId}/qa/pre-submission/validate`
  );
}

/**
 * Validate state transition
 */
export async function validateTransition(
  reportId: string,
  fromStatus: string,
  toStatus: string
): Promise<TransitionValidation> {
  const client = getAPIClient();
  return client.post<TransitionValidation>(
    `/reports/${reportId}/qa/validate-transition`,
    { from_status: fromStatus, to_status: toStatus }
  );
}

/** PII scan match (email, proper_noun, blocklist) */
export interface PIIMatch {
  type: 'email' | 'proper_noun' | 'blocklist';
  value: string;
  snippet: string;
  section_id?: string | null;
}

export interface PIIScanResult {
  matches: PIIMatch[];
  scanned_at: string;
  section_count: number;
  error?: string;
  total_count?: number;
  truncated?: boolean;
}

export interface PIIApproveResult {
  pii_reviewed_at: string;
  pii_reviewed_by: string;
}

/**
 * Scan report content for PII and proper nouns
 */
export async function piiScanReport(reportId: string): Promise<PIIScanResult> {
  const client = getAPIClient();
  return client.post<PIIScanResult>(`/reports/${reportId}/pii-scan`);
}

/**
 * Mark report as PII-reviewed; optionally updates project allowlist
 */
export async function piiApproveReport(reportId: string): Promise<PIIApproveResult> {
  const client = getAPIClient();
  return client.post<PIIApproveResult>(`/reports/${reportId}/pii-approve`);
}

/**
 * Clear PII-reviewed status so the report can be re-scanned and re-approved
 */
export async function piiDisapproveReport(reportId: string): Promise<{ pii_reviewed_at: null; pii_reviewed_by: null }> {
  const client = getAPIClient();
  return client.post<{ pii_reviewed_at: null; pii_reviewed_by: null }>(`/reports/${reportId}/pii-disapprove`);
}

/**
 * Add a single term to the project PII allowlist (project-based; applies to all reports in the project)
 */
export async function piiAddToAllowlist(reportId: string, term: string): Promise<{ added: boolean; term: string }> {
  const client = getAPIClient();
  return client.post<{ added: boolean; term: string }>(`/reports/${reportId}/pii-allowlist`, { term });
}

// ---------------------------------------------------------------------------
// Score Readiness (POST /reports/{report_id}/evaluate)
// ---------------------------------------------------------------------------

/** Per-criterion score returned by the C.R.A.F.T. evaluation. */
export interface CriterionScore {
  criterion_id: string;
  title: string;
  max_points: number;
  score: number;
  rationale?: string;
}

/** A gap surfaced by the evaluator. */
export interface GapItem {
  section_reference: string;
  severity: 'critical' | 'major' | 'minor';
  gap_type: 'missing_section' | 'underdeveloped' | 'claim_lacking_evidence';
  description: string;
  recommendation?: string;
}

/** One row in the criterion scoring matrix (evaluate response). */
export interface ScoringMatrixCriterionRow {
  label: string;
  rfp_reference: string;
  alignment: 'Strong' | 'Partial' | 'Poor';
  score: number;
  max_score: number;
  gap_analysis?: string | null;
}

/** Structured scoring matrix for Score Readiness UI. */
export interface DetailedScoringMatrix {
  criteria: ScoringMatrixCriterionRow[];
  critical_missing_sections: string[];
  overall_readiness_pct?: number;
}

/** A high-impact recommendation ranked by recoverable points. */
export interface HighImpactFix {
  id: string;
  section_reference: string;
  severity: 'critical' | 'major' | 'minor';
  gap_type: string;
  description: string;
  recommendation?: string;
  estimated_points_recoverable: number;
}

/**
 * Response from POST /reports/{report_id}/evaluate.
 *
 * The same payload is returned for both the legacy zero-arg form (which
 * scores the persisted v2/v1 report against the MOTI rubric) and the
 * extended file-upload form (which scores an uploaded proposal against an
 * optional custom rubric).
 */
export interface ProposalEvaluationResponse {
  report_id: string;
  total_score: number;
  max_total: number;
  criterion_scores: CriterionScore[];
  gap_analysis: GapItem[];
  missing_sections: string[];
  evaluated_at: string;
  evaluation_source: 'craft' | 'craft_custom_rubric' | 'craft_template_derived' | 'craft_inferred';
  top_high_impact_fixes: HighImpactFix[];
  /** 0..1 readiness ratio (total_score / max_total, with target weighting). */
  overall_readiness_pct: number;
  /** Points still needed to hit the target score; never negative. */
  gap_to_target_points: number;
  /** 0..1 target the readiness was scored against (default 0.85). */
  target_score_pct?: number;
  /** Per-criterion alignment matrix for the Score Readiness panel. */
  detailed_scoring_matrix?: DetailedScoringMatrix;
  /** Mismatch vs user-confirmed staff/project selections. */
  selection_alignment?: {
    staff_mismatches?: Array<Record<string, unknown>>;
    project_mismatches?: Array<Record<string, unknown>>;
  };
}

/** Build matrix client-side when older cached evaluations omit the field. */
export function buildDetailedScoringMatrix(
  data: ProposalEvaluationResponse
): DetailedScoringMatrix {
  if (data.detailed_scoring_matrix?.criteria?.length) {
    return data.detailed_scoring_matrix;
  }
  const gapsBySection: Record<string, string[]> = {};
  for (const gap of data.gap_analysis || []) {
    const ref = gap.section_reference;
    gapsBySection[ref] = gapsBySection[ref] || [];
    gapsBySection[ref].push(gap.description);
  }
  const criteria = (data.criterion_scores || []).map((cs) => {
    const ratio = cs.max_points > 0 ? cs.score / cs.max_points : 0;
    const alignment: ScoringMatrixCriterionRow['alignment'] =
      ratio >= 0.7 ? 'Strong' : ratio >= 0.4 ? 'Partial' : 'Poor';
    let gapTexts = gapsBySection[cs.criterion_id] || [];
    if (!gapTexts.length) {
      gapTexts = gapsBySection[`${cs.criterion_id} ${cs.title}`] || [];
    }
    return {
      label: cs.title,
      rfp_reference: cs.criterion_id,
      alignment,
      score: cs.score,
      max_score: cs.max_points,
      gap_analysis: gapTexts.length ? gapTexts.join('; ') : cs.rationale ?? null,
    };
  });
  const critical_missing_sections = [
    ...new Set(
      (data.gap_analysis || [])
        .filter((g) => g.severity === 'critical' && g.gap_type === 'missing_section')
        .map((g) => g.section_reference)
    ),
  ].sort();
  return {
    criteria,
    critical_missing_sections,
    overall_readiness_pct: data.overall_readiness_pct,
  };
}

export interface ScoreReadinessParams {
  reportId: string;
  /** Optional uploaded proposal file (PDF/DOCX/MD/TXT). */
  proposalFile?: File;
  /** Optional custom-rubric JSON file. */
  criteriaFile?: File;
  /** Optional inline custom-rubric JSON string (alternative to criteriaFile). */
  criteriaJson?: string;
  /** Optional target readiness percentage (0..1). Default backend value is 0.85. */
  targetScorePct?: number;
  /** Bypass server-side evaluation cache and re-run LLM scoring. */
  forceRefresh?: boolean;
}

/**
 * Score the readiness of a proposal against the technical rubric.
 *
 * Two modes share one endpoint:
 *  - Legacy / "Score this proposal": no body. Server evaluates the persisted
 *    v2 (or v1) report content against the MOTI rubric.
 *  - Extended / "Score an external file": multipart/form-data with an
 *    uploaded file and/or custom rubric and/or target score override.
 *
 * Backend route: POST /reports/{report_id}/evaluate (mounted at /reports;
 * the public Trace API spec at https://trace-api.alpyne.ai/docs documents
 * it as /api/reports/{report_id}/evaluate when proxied through nginx).
 */
export async function scoreReadiness(
  params: ScoreReadinessParams
): Promise<ProposalEvaluationResponse> {
  const { reportId, proposalFile, criteriaFile, criteriaJson, targetScorePct, forceRefresh } =
    params;
  const client = getAPIClient();
  const url = `/reports/${reportId}/evaluate`;
  const refreshConfig = forceRefresh ? { params: { force_refresh: true } } : undefined;

  const hasMultipartPayload =
    !!proposalFile || !!criteriaFile || !!criteriaJson || typeof targetScorePct === 'number';

  if (!hasMultipartPayload) {
    return client.post<ProposalEvaluationResponse>(url, undefined, refreshConfig);
  }

  const formData = new FormData();
  if (proposalFile) {
    formData.append('proposal_file', proposalFile);
  }
  if (criteriaFile) {
    formData.append('criteria_file', criteriaFile);
  }
  if (criteriaJson) {
    formData.append('criteria_json', criteriaJson);
  }
  if (typeof targetScorePct === 'number') {
    formData.append('target_score_pct', String(targetScorePct));
  }

  return client.post<ProposalEvaluationResponse>(url, formData, refreshConfig);
}
